import type { UserGetSubtreeResponse } from 'api/user/User.schemas.ts';
import { GET } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { Button } from 'ui/component/Button.tsx';
import { INoResults, ISearch, ITable } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Input } from 'ui/control/Input.tsx';
import { HighlightMatchingText } from '#admin/component/HighlightMatchingText.tsx';
import { type NodeState, type NodesState, Tree, type TreeNode } from '#admin/component/Tree.tsx';
import { useMe } from '#admin/hook/useMe.tsx';

type UserNode = {
  id: string;
  owner_id: string | null;
  is_group: boolean;
  name: string;
  attributes: { ID: string; EXID: string; role: string; providerId: string | null; email: string };
  users_list: string[];
};

type FormValues = {
  search: string;
};

export const GroupDiagram = () => {
  const { t } = useTranslation();
  const { me } = useMe();
  const navigate = useNavigate();
  const [nodesState, setNodesState] = React.useState<NodesState>({});
  const [isEmptyResults, setIsEmptyResults] = React.useState<boolean>(false);
  const { data, isLoading } = useSWR<UserGetSubtreeResponse>(me ? [`/user/${me.owner_id || me.id}/subtree`] : null, GET);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQueryParam = searchParams.get('search') || '';

  const getNodeKey = React.useCallback((node: TreeNode<UserNode>) => node.data.id, []);

  const hierarchy = React.useMemo(() => {
    if (!me || !data) return [];

    const userNodes: TreeNode<UserNode>[] = data.map((user) => ({
      data: {
        id: user.id,
        owner_id: user.owner_id,
        is_group: user.is_group,
        name: user.name,
        attributes: { ID: user.id.slice(0, 8), EXID: user.logistics_ex_id || '-', role: user.role, providerId: user.logistics_ex_id, email: user.email },
        users_list: [],
      },
      children: [],
    }));

    const root = userNodes.find((node) => node.data.id === me.owner_id);
    if (!root) throw new Error('Root node not found');

    const tree = buildUserTree(root, userNodes);
    const state = computeNodesState(tree, getNodeKey, '');
    setNodesState(state);
    return tree;
  }, [me, data, getNodeKey]);

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
    defaultValues: { search: searchQueryParam },
  });

  const onNodeStateChange = React.useCallback((nodeKey: string, nodeState: NodeState) => {
    setNodesState((prev) => ({ ...prev, [nodeKey]: nodeState }));
  }, []);

  const renderNode = React.useCallback(
    (node: TreeNode<UserNode>) => {
      return (
        <Link to={`/user/${node.data.id}`} className="group flex min-w-[320px] grow items-center gap-4 transition-all!">
          <h6 className="min-w-[140px] truncate text-grey-dark transition-all group-hover:text-blue">
            <HighlightMatchingText search={searchQueryParam}>{node.data.name}</HighlightMatchingText>
          </h6>

          <div className="flex items-center gap-1.5">
            <span className="caption text-grey-label">{t('labels.id')}:</span>
            <span className="caption font-mono text-grey">
              <HighlightMatchingText search={searchQueryParam}>{node.data.attributes.EXID}</HighlightMatchingText>
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="caption text-grey-label">{t('labels.role')}:</span>
            <span className="body4 text-blue">
              <HighlightMatchingText search={searchQueryParam}>{node.data.attributes.role}</HighlightMatchingText>
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="caption text-grey-label">{t('labels.users')}:</span>
            {node.data.users_list.length > 0 ? (
              <span className="caption w-fit rounded bg-blue-light px-2 py-0.5 text-blue">{node.data.users_list.length}</span>
            ) : (
              <span className="body2 text-grey-dark">0</span>
            )}
          </div>
        </Link>
      );
    },
    [t, searchQueryParam],
  );

  const onSearch = React.useCallback(
    (values: FormValues) => {
      setSearchParams(values.search ? { search: values.search } : {}, { replace: true });
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    const newState = computeNodesState(hierarchy, getNodeKey, searchQueryParam);
    setNodesState(newState);
    setIsEmptyResults(Object.keys(newState).length === 0 && Boolean(searchQueryParam));
  }, [hierarchy, getNodeKey, searchQueryParam]);

  if (!me || isLoading) <Spinner centered={true} />;
  if (!data || !data.length) return null;

  return (
    <main className="view-container">
      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('views.groupDiagram.title')}</h4>
        <div className="flex flex-row gap-2">
          <Button className="blue-outlined" LeftIcon={ITable} onClick={() => navigate('/groups')}>
            {t('views.groupDiagram.tableView')}
          </Button>
        </div>
      </section>

      <section className="mt-4 flex flex-col gap-4 bg-white p-8 pb-4">
        <FormProvider {...formMethods}>
          <form id="search-form" onSubmit={formMethods.handleSubmit(onSearch)}>
            <Input
              className="w-[360px]"
              name={'search'}
              placeholder={t('placeholders.searchGroups')}
              required={false}
              autoFocus={true}
              RightIcon={ISearch}
              autoComplete={'off'}
              debouncedAutoSubmitForm={'#search-form'}
              registerOptions={{ required: false }}
              aria-required={false}
            />
          </form>
        </FormProvider>
        {isEmptyResults ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <INoResults className="text-grey-label" size={48} />
            <div className="flex flex-col items-center gap-1">
              <h6 className="text-grey-dark">{t('views.groupDiagram.noResults')}</h6>
              <span className="body2 text-grey">Try adjusting your search terms</span>
            </div>
          </div>
        ) : (
          <Tree nodes={hierarchy} renderNode={renderNode} getNodeKey={getNodeKey} nodesState={nodesState} onNodeStateChange={onNodeStateChange} />
        )}
      </section>
    </main>
  );
};

export const buildUserTree = (user: TreeNode<UserNode>, users: TreeNode<UserNode>[]): TreeNode<UserNode>[] => {
  const children = users.filter((u) => u.data.owner_id === user.data.id);
  children.forEach((node) => buildUserTree(node, users));
  children.sort((a, b) => b.children.length - a.children.length);

  // we can't render every user or tree would be impossible to see, so we only render the groups and have a recap of users in the group and give the number of users in the group
  for (const group of [...children.filter((node) => node.data.is_group), user]) {
    // we need to compute the user list for the current user also
    const group_users = users.filter((node) => node.data.owner_id === group.data.id && !node.data.is_group);
    const users_list = group_users.map((node) => node.data.name).sort();
    group.data.users_list = users_list;
  }

  user.children = children.filter((node) => node.data.is_group);
  return [user];
};

export const filterNode = (node: UserNode, search: string) => {
  return node.name.toLowerCase().includes(search.toLowerCase()) || Object.values(node.attributes).some((value) => value?.toString().toLowerCase().includes(search.toLowerCase()));
};

export const computeNodesState = (tree: TreeNode<UserNode>[], getNodeKey: (node: TreeNode<UserNode>) => string, search: string): NodesState => {
  if (!search) {
    // Only return the roots
    return tree.reduce((acc, node) => {
      acc[getNodeKey(node)] = { expanded: false };
      return acc;
    }, {} as NodesState);
  }

  const traverse = (nodes: TreeNode<UserNode>[]): NodesState => {
    let result: NodesState = {};

    for (const node of nodes) {
      const isMatch = filterNode(node.data, search);
      const matchingChildren = traverse(node.children);

      // One or more children match, so we need to expand the node
      if (Object.keys(matchingChildren).length > 0) {
        result[getNodeKey(node)] = { expanded: true };
        result = { ...result, ...matchingChildren };
      } else if (isMatch) {
        // Node itself matches, include it but don't expand it
        result[getNodeKey(node)] = { expanded: false };
      }
    }

    return result;
  };

  return traverse(tree);
};
